import { useMemo, useState } from "react";
import {
  useGetSettingsQuery,
  usePatchSettingsMutation,
  useGetDcaHistoryQuery,
  useRunDcaNowMutation,
  useSendNotificationsTestMutation,
  type DcaRun,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { fmtUsd, fmtTime, fmtRelative } from "@/lib/format";
import { toast } from "sonner";
import { RefreshCw, Play } from "lucide-react";

/** DCA control panel. Operator can toggle the engine, edit symbols /
 *  per-run amount / cadence, force a tick now, and see recent run
 *  history. Per the design: DCA is independent of the scalp/hourly
 *  engines and of the auto_defense, so it can run even while those
 *  are paused. That's the whole point — average cost basis through
 *  every market. */
export default function Dca() {
  const { data: settings, isLoading: settingsLoading } = useGetSettingsQuery();
  const [patch, { isLoading: saving }] = usePatchSettingsMutation();
  const history = useGetDcaHistoryQuery(undefined, { pollingInterval: 30000 });
  const [runNow, { isLoading: running }] = useRunDcaNowMutation();

  const s = (settings ?? {}) as Record<string, unknown>;
  const enabled = Boolean(s.dca_engine_enabled);
  const symbols = Array.isArray(s.dca_symbols) ? (s.dca_symbols as string[]) : [];
  const amountUsd = Number(s.dca_amount_usd_per_run ?? 30);
  const intervalHours = Number(s.dca_interval_hours ?? 24);

  // Local editor state — initialized from settings, mutated by inputs.
  const [editSymbols, setEditSymbols] = useState<string>(symbols.join(", "));
  const [editAmount, setEditAmount] = useState<string>(String(amountUsd));
  const [editInterval, setEditInterval] = useState<string>(String(intervalHours));

  // Re-seed editor when settings load (only first-time).
  useMemo(() => {
    if (settings && !settingsLoading) {
      setEditSymbols(symbols.join(", "));
      setEditAmount(String(amountUsd));
      setEditInterval(String(intervalHours));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoading]);

  const onToggle = async () => {
    try {
      await patch({ dca_engine_enabled: !enabled }).unwrap();
      toast.success(`DCA ${!enabled ? "enabled" : "disabled"}`);
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Toggle failed");
    }
  };

  const onSave = async () => {
    const parsedSymbols = editSymbols
      .split(",")
      .map((x) => x.trim().toUpperCase())
      .filter(Boolean);
    const amt = parseFloat(editAmount);
    const interval = parseInt(editInterval, 10);
    if (!parsedSymbols.length || parsedSymbols.some((s) => !s.includes("/"))) {
      toast.error("Symbols must be 'BASE/QUOTE' format, comma-separated");
      return;
    }
    if (!Number.isFinite(amt) || amt < 5) {
      toast.error("Amount must be ≥ $5");
      return;
    }
    if (!Number.isInteger(interval) || interval < 1) {
      toast.error("Interval must be a positive integer (hours)");
      return;
    }
    try {
      await patch({
        dca_symbols: parsedSymbols,
        dca_amount_usd_per_run: amt,
        dca_interval_hours: interval,
      }).unwrap();
      toast.success("DCA settings saved");
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Save failed");
    }
  };

  const onRunNow = async () => {
    if (!confirm("Force a DCA buy NOW? This places real market orders.")) return;
    try {
      const r = await runNow().unwrap();
      if (r.status === "ok") {
        const n = (r.fills ?? []).filter((f) => f.status === "ok").length;
        toast.success(`DCA tick OK — ${n} fill${n === 1 ? "" : "s"}`);
      } else if (r.status === "skipped") {
        toast.warning(`DCA skipped: ${r.reason}`);
      } else {
        toast.error(`DCA ${r.status}: ${r.reason ?? "see history"}`);
      }
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Run failed");
    }
  };

  const runs = (history.data ?? []) as DcaRun[];
  const perSymbolUsd = symbols.length > 0 ? amountUsd / symbols.length : 0;

  return (
    <div className="space-y-4">
      {/* Header / status */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              DCA engine
              <Badge tone={enabled ? "good" : "muted"}>
                {enabled ? "ENABLED" : "disabled"}
              </Badge>
            </CardTitle>
            <p className="text-xs text-muted mt-1">
              Independent of scalp / hourly engines and of auto_defense.
              Buys a fixed USD amount split equally across the symbols
              on a fixed cadence — no signals, no stops.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm"
                    variant={enabled ? "ghost" : "primary"}
                    disabled={saving}
                    onClick={onToggle}>
              {enabled ? "Disable" : "Enable"}
            </Button>
            <Button size="sm" variant="primary"
                    disabled={!enabled || running}
                    onClick={onRunNow}>
              <Play className="w-3.5 h-3.5" /> Run now
            </Button>
            <Button size="sm" variant="ghost"
                    onClick={() => history.refetch()}>
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {settingsLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat label="Per-run total"   value={fmtUsd(amountUsd)} />
              <Stat label="Per-symbol slice" value={fmtUsd(perSymbolUsd)} />
              <Stat label="Cadence"          value={`${intervalHours}h`} />
              <Stat label="Symbols"          value={`${symbols.length}`} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor */}
      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert tone="info">
            Saves go through <code>PATCH /api/settings</code>. New cadence
            takes effect on the next hourly cron tick — no restart needed.
          </Alert>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Symbols (BASE/QUOTE, comma-separated)</Label>
              <Input value={editSymbols}
                      onChange={(e) => setEditSymbols(e.target.value)}
                      placeholder="BTC/USDT, ETH/USDT, BNB/USDT" />
              <p className="text-xs text-muted mt-1">
                Amount is split equally across these.
              </p>
            </div>
            <div>
              <Label>Total USD per run</Label>
              <Input type="number" step="1" min="5"
                      value={editAmount}
                      onChange={(e) => setEditAmount(e.target.value)} />
              <p className="text-xs text-muted mt-1">
                Per-symbol slice = total ÷ {symbols.length || 1}. Must be
                ≥ $5 per symbol (Binance dust floor).
              </p>
            </div>
            <div>
              <Label>Interval (hours)</Label>
              <Input type="number" step="1" min="1"
                      value={editInterval}
                      onChange={(e) => setEditInterval(e.target.value)} />
              <p className="text-xs text-muted mt-1">
                Default 24h. Cron polls every hour but the engine itself
                only fires when this much time has passed since the last
                successful run.
              </p>
            </div>
          </div>
          <Alert tone="info">
            <strong>Multi-user.</strong> These are the global universe +
            cadence settings. Each user opts in independently on their own
            user-settings page (or admin can toggle for them at{" "}
            <code>/admin/users/{`{uid}`}</code>). Each opted-in user runs
            the same buy logic on their own Binance keys, with their own
            cadence tracking. Per-user amount overrides supported.
          </Alert>
          <div className="flex gap-2">
            <Button size="sm" variant="primary" disabled={saving} onClick={onSave}>
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <NotificationsCard />

      {/* Run history */}
      <Card>
        <CardHeader>
          <CardTitle>
            Recent runs
            <span className="text-xs text-muted ml-2 font-normal">
              {runs.length} entries
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : runs.length === 0 ? (
            <p className="text-sm text-muted">
              No runs yet. Hit "Run now" to test a tick, or wait for the next
              cadence interval to elapse.
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Time</TH>
                  <TH>Owner</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Cost</TH>
                  <TH className="text-right">Current value</TH>
                  <TH className="text-right">Unrealized P&amp;L</TH>
                  <TH>Fills</TH>
                </TR>
              </THead>
              <TBody>
                {runs.map((r) => (
                  <DcaRunRow key={r.id} run={r} />
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function NotificationsCard() {
  const { data: settings } = useGetSettingsQuery();
  const [patch, { isLoading: saving }] = usePatchSettingsMutation();
  const [sendTest, { isLoading: testing }] = useSendNotificationsTestMutation();
  const s = (settings ?? {}) as Record<string, unknown>;
  const enabled = Boolean(s.notify_email_enabled);
  const recipients = Array.isArray(s.notify_email_recipients)
    ? (s.notify_email_recipients as string[]) : [];
  const flags = {
    dca: Boolean(s.notify_dca ?? true),
    defense: Boolean(s.notify_defense ?? true),
    emergency: Boolean(s.notify_emergency_close ?? true),
  };
  const [editRecipients, setEditRecipients] = useState<string>(recipients.join(", "));
  useMemo(() => { setEditRecipients(recipients.join(", ")); /* eslint-disable-next-line */ }, [recipients.join(",")]);

  const onToggle = async (key: string, current: boolean) => {
    try {
      await patch({ [key]: !current }).unwrap();
      toast.success(`${key} ${!current ? "on" : "off"}`);
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Toggle failed");
    }
  };

  const onSaveRecipients = async () => {
    const list = editRecipients
      .split(",").map((x) => x.trim()).filter((x) => x.includes("@"));
    try {
      await patch({ notify_email_recipients: list }).unwrap();
      toast.success(`Saved ${list.length} recipient(s)`);
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Save failed");
    }
  };

  const onTest = async () => {
    try {
      const r = await sendTest().unwrap();
      if (r.status === "ok") {
        toast.success(`Test sent to ${(r.sent_to ?? []).join(", ")}`);
      } else {
        toast.warning(`Test ${r.status}: ${r.reason ?? ""}`);
      }
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Test failed");
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 flex-wrap">
        <div>
          <CardTitle className="flex items-center gap-2 flex-wrap">
            Email notifications
            <Badge tone={enabled ? "good" : "muted"}>
              {enabled ? "ON" : "off"}
            </Badge>
          </CardTitle>
          <p className="text-xs text-muted mt-1">
            Sends on DCA fills, auto-defense engage/release, and trade-health
            emergency-closes. Master switch overrides all per-event toggles.
            Requires SMTP configured in the env (SMTP_HOST/USER/PASSWORD/FROM).
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm"
                  variant={enabled ? "ghost" : "primary"}
                  disabled={saving}
                  onClick={() => onToggle("notify_email_enabled", enabled)}>
            {enabled ? "Disable" : "Enable"}
          </Button>
          <Button size="sm" variant="ghost"
                  disabled={testing}
                  onClick={onTest}>
            Send test email
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Recipients (comma-separated emails)</Label>
          <div className="flex gap-2">
            <Input value={editRecipients}
                    onChange={(e) => setEditRecipients(e.target.value)}
                    placeholder="ops@example.com, you@example.com" />
            <Button size="sm" variant="primary"
                    disabled={saving}
                    onClick={onSaveRecipients}>Save</Button>
          </div>
          <p className="text-xs text-muted mt-1">
            Leave empty to fall back to the configured admin alert
            recipient (env <code>ALERT_EMAIL</code> or admin's user email).
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <EventToggle label="DCA fills"
                        on={flags.dca}
                        onClick={() => onToggle("notify_dca", flags.dca)}
                        disabled={!enabled || saving} />
          <EventToggle label="Defense engage / release"
                        on={flags.defense}
                        onClick={() => onToggle("notify_defense", flags.defense)}
                        disabled={!enabled || saving} />
          <EventToggle label="Emergency close"
                        on={flags.emergency}
                        onClick={() => onToggle("notify_emergency_close", flags.emergency)}
                        disabled={!enabled || saving} />
        </div>
      </CardContent>
    </Card>
  );
}

function EventToggle({ label, on, onClick, disabled }: {
  label: string; on: boolean; onClick: () => void; disabled: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={
        "border border-border rounded-md p-2 text-left transition " +
        (disabled ? "opacity-50 cursor-not-allowed " : "hover:bg-bg2 ") +
        (on ? "bg-bg2/30" : "")
      }
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs">{label}</span>
        <Badge tone={on ? "good" : "muted"}>{on ? "on" : "off"}</Badge>
      </div>
    </button>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border rounded-md p-2 bg-bg2/30">
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold tabular">{value}</div>
    </div>
  );
}

function DcaRunRow({ run }: { run: DcaRun }) {
  const fills = useMemo(() => {
    try { return JSON.parse(run.fills_json || "[]"); }
    catch { return []; }
  }, [run.fills_json]);
  const tone =
    run.status === "ok" ? "good"
    : run.status === "skipped" ? "muted"
    : "bad";
  const upnl = run.run_unrealized_pnl_usd;
  const upnlPct =
    run.run_unrealized_pnl_usd != null && run.run_cost_usd
      ? (run.run_unrealized_pnl_usd / run.run_cost_usd) * 100
      : null;
  const upnlClass =
    upnl == null ? "text-muted"
    : upnl >= 0 ? "text-good" : "text-bad";
  return (
    <TR>
      <TD className="text-xs text-muted">
        {fmtTime(run.ts)}
        <div className="text-muted/60">{fmtRelative(run.ts)}</div>
      </TD>
      <TD className="text-xs">
        {run.user_id != null ? (
          <>
            <span className="text-muted">#{run.user_id}</span>{" "}
            <span>{run.user_email ?? ""}</span>
          </>
        ) : <span className="text-muted/60">—</span>}
      </TD>
      <TD><Badge tone={tone}>{run.status}</Badge></TD>
      <TD className="text-right tabular">{fmtUsd(run.run_cost_usd ?? run.amount_usd_per_run ?? null)}</TD>
      <TD className="text-right tabular">
        {run.run_current_value_usd != null ? fmtUsd(run.run_current_value_usd) : "—"}
      </TD>
      <TD className={"text-right tabular " + upnlClass}>
        {upnl == null ? "—" : (
          <>
            {upnl >= 0 ? "+" : ""}{fmtUsd(upnl)}
            {upnlPct != null && (
              <div className="text-xs">
                {upnlPct >= 0 ? "+" : ""}{upnlPct.toFixed(2)}%
              </div>
            )}
          </>
        )}
      </TD>
      <TD className="text-xs">
        {fills.map((f: any, i: number) => {
          const fUpnl = f.unrealized_pnl_usd;
          const fUpnlClass =
            fUpnl == null ? "text-muted/70"
            : fUpnl >= 0 ? "text-good" : "text-bad";
          return (
            <div key={i} className={f.status === "ok" ? "" : "text-muted"}>
              <span className="font-medium">{f.symbol}</span>
              {f.status === "ok" && f.avg_price ? (
                <>
                  <span className="text-muted/70">
                    {" "}@{Number(f.avg_price).toFixed(6)}
                  </span>
                  {fUpnl != null && (
                    <span className={" " + fUpnlClass}>
                      {" "}({fUpnl >= 0 ? "+" : ""}{fmtUsd(fUpnl)})
                    </span>
                  )}
                </>
              ) : (
                <span className="text-muted/70"> ({f.status}{f.reason ? `: ${f.reason}` : ""})</span>
              )}
            </div>
          );
        })}
      </TD>
    </TR>
  );
}
